import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addRegion,
  deleteRegion,
  fetchRegionDetails,
  fetchRegions,
  updateRegion,
} from '../../common/api/regionsService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { TranslationItem } from '../../common/types/translations.types';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface RegionDetails {
  id?: string;
  isDeleted?: boolean;
  countryId: string;
  nameTranslations: TranslationItem[];
  descriptionTranslations: TranslationItem[];
  metaTitleTranslations: TranslationItem[];
  metaDescriptionTranslations: TranslationItem[];
}

export interface RegionListItem {
  id: string;
  name: string;
}

export interface RegionsFilter extends PaginationParameters {
  searchString: string;
}

export interface RegionsState {
  regionItems: RegionListItem[];
  regionDetails: RegionDetails;
  regionItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  regionDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  regionItemsPagination: PaginationResponse;
}

const initialState: RegionsState = {
  regionItems: [],
  regionDetails: {
    id: '',
    isDeleted: false,
    countryId: '',
    nameTranslations: [],
    descriptionTranslations: [],
    metaTitleTranslations: [],
    metaDescriptionTranslations: [],
  },
  regionItemsStatus: LOADING_STATUS.IDLE,
  regionDetailsStatus: LOADING_STATUS.IDLE,
  regionItemsPagination: InitialPaginationResponse,
};

export const getRegionsAsync = createAsyncThunk(
  'regions/getRegions',
  async (regionsFilter: RegionsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchRegions(regionsFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getRegionDetailsAsync = createAsyncThunk(
  'regions/getRegionDetails',
  async (regionId: string, { rejectWithValue }) => {
    try {
      const response = await fetchRegionDetails(regionId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveRegionAsync = createAsyncThunk(
  'regions/addRegion',
  async (regionData: RegionDetails, { rejectWithValue }) => {
    try {
      const response = await addRegion(regionData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateRegionAsync = createAsyncThunk(
  'regions/updateRegion',
  async (regionData: RegionDetails, { rejectWithValue }) => {
    try {
      const response = await updateRegion(regionData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteRegionAsync = createAsyncThunk(
  'regions/deleteRegion',
  async (regionId: string, { rejectWithValue }) => {
    try {
      const response = await deleteRegion(regionId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const regionsSlice = createSlice({
  name: 'regions',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getRegionsAsync.pending, state => {
        state.regionItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getRegionsAsync.fulfilled, (state, action) => {
        state.regionItemsStatus = LOADING_STATUS.IDLE;
        state.regionItems = action.payload.data;
        state.regionItemsPagination = PopulatePagination(action.payload);
      })
      .addCase(getRegionsAsync.rejected, state => {
        state.regionItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getRegionDetailsAsync.pending, state => {
        state.regionDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getRegionDetailsAsync.fulfilled, (state, action) => {
        state.regionDetailsStatus = LOADING_STATUS.IDLE;
        state.regionDetails = action.payload;
      })
      .addCase(getRegionDetailsAsync.rejected, state => {
        state.regionDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectRegionItems = (state: RootState) =>
  state.regions.regionItems;
export const selectRegionItemsPagination = (state: RootState) =>
  state.regions.regionItemsPagination;
export const selectRegionItemsLoadingStatus = (state: RootState) =>
  state.regions.regionItemsStatus;
export const selectRegionDetails = (state: RootState) =>
  state.regions.regionDetails;
export const selectRegionDetailsLoadingStatus = (state: RootState) =>
  state.regions.regionDetailsStatus;

export default regionsSlice.reducer;
